// @erp/module-cms/schemas — the NEWSLETTER subscriber wire contracts. Browser-safe:
// zod + plain data only (the static site's signup form and the gateway parse the SAME
// schemas, so the form and the server can never disagree on what a valid subscription
// is). This slice is LOCAL subscriber capture; the ESP wiring (newsletter_provider /
// newsletter_api_key / newsletter_list_id in schemas/settings.ts) is a documented
// followup that would SYNC these local rows outward — not part of the capture contract.
//
// PRIVACY: the PUBLIC subscribe/unsubscribe endpoints are DELIBERATELY non-enumerating —
// both answer a minimal { ok: true } and never reveal whether an email was already on
// file. The email is NORMALIZED here (trim + lowercase) so the UNIQUE(email) key and the
// unsubscribe lookup can never split one address across two rows / miss on a case variant.
import { z } from "zod";
import { CursorQuery, EntityId, EpochMs } from "@erp/core-schemas";

// ── The subscription status (a fixed set ⇒ an enum, so a bad value 400s) ────────
export const SUBSCRIBER_STATUSES = ["subscribed", "unsubscribed"] as const;
export const SubscriberStatus = z.enum(SUBSCRIBER_STATUSES);
export type SubscriberStatus = z.infer<typeof SubscriberStatus>;

/**
 * The normalized email field — trim + lowercase BEFORE validating the RFC shape, so
 * "  Alice@Example.COM " stores as "alice@example.com". The lowercase form is what the
 * UNIQUE(email) index dedupes on and what unsubscribe looks up, so case/whitespace can
 * never fork one subscriber into two rows or leak via a lookup miss. 254 = the RFC 5321
 * path limit (the modules/lib members.ts precedent).
 */
const emailField = z.string().trim().toLowerCase().pipe(z.email().max(254));

/** Bounded optional free text — the max keeps a runaway value out of the store. */
const shortText = z.string().trim().min(1).max(200);

/**
 * PUBLIC subscribe body — the external site's signup form. email required; name +
 * source optional (source = a form id / campaign tag). Subscribing an EXISTING email is
 * a success (the service upserts → reactivates), never a duplicate-email error.
 */
export const SubscribeBody = z.object({
  email: emailField,
  name: shortText.optional(),
  source: shortText.optional(),
});
export type SubscribeBody = z.infer<typeof SubscribeBody>;

/** PUBLIC unsubscribe body — email only. An unknown email is a QUIET success (no enumeration). */
export const UnsubscribeBody = z.object({
  email: emailField,
});
export type UnsubscribeBody = z.infer<typeof UnsubscribeBody>;

/** The admin wire DTO — the read shape (camelCase). There are no audit actor columns (capture is anonymous). */
export const CmsSubscriber = z.object({
  id: EntityId,
  email: z.string(),
  name: z.string().nullable(),
  status: SubscriberStatus,
  source: z.string().nullable(),
  createdAt: EpochMs,
  updatedAt: EpochMs,
  unsubscribedAt: EpochMs.nullable(),
});
export type CmsSubscriber = z.infer<typeof CmsSubscriber>;

/**
 * The admin list query — keyset cursor+limit + optional ?status exact-eq + ?search over
 * email/name (the media ?search precedent: trimmed, bounded, LIKE-escaped in sql.ts).
 */
export const CmsSubscriberListQuery = CursorQuery.extend({
  status: SubscriberStatus.optional(),
  search: shortText.optional(),
});
export type CmsSubscriberListQuery = z.infer<typeof CmsSubscriberListQuery>;
